import { liveQuery } from "dexie";
import { useEffect, useState } from "react";
import { createPortal } from "react-dom";

import type {
  StockMovementHistoryRuntimeItem,
  StockRuntimeItem,
} from "../runtime/gateway/local-projection-client";
import { readLocalEmployeeSession, type LocalEmployeeSessionState } from "../runtime/local-store/employee-session";
import {
  indexedScope,
  readLocalProjectionCursor,
  searchLocalStock,
} from "../runtime/local-store/indexed-runtime";
import { posRuntimeDb } from "../runtime/local-store/pos-db";
import { readStockActionRuntime } from "../runtime/local-store/stock-action-runtime";
import { readLocalStockMovementHistory } from "../runtime/local-store/stock-movement-runtime";
import { readTerminalCredential, type TerminalCredentialState } from "../runtime/local-store/terminal-state";
import { StockActionPanel } from "./StockActionPanel";
import { StockInternalUseBatch } from "./StockInternalUseBatch";

type RuntimeSnapshot = {
  terminal: TerminalCredentialState | null;
  session: LocalEmployeeSessionState | null;
};

type GroupedStockRuntimeItem = StockRuntimeItem & {
  stock_group_id?: string | null;
  stock_group_name?: string | null;
  stock_group_sort_order?: number | null;
  stock_group_status?: "ACTIVE" | "INACTIVE" | null;
};

type LocalStockGroup = {
  id: string;
  name: string;
  sortOrder: number;
};

type LocalStockState = {
  items: GroupedStockRuntimeItem[];
  groups: LocalStockGroup[];
  hasUnassigned: boolean;
  hasLocalSnapshot: boolean;
};

type LocalStockDetailState = {
  item: GroupedStockRuntimeItem | null;
  movements: StockMovementHistoryRuntimeItem[];
  hasHistorySnapshot: boolean;
};

type BatchComposerState = {
  items: GroupedStockRuntimeItem[];
  seedItem: GroupedStockRuntimeItem | null;
};

const SEARCH_LIMIT = 500;

function quantityLabel(value: number | null) {
  if (value == null) return "—";
  return new Intl.NumberFormat("th-TH", { maximumFractionDigits: 3 }).format(value);
}

function deltaLabel(value: number) {
  const number = Number(value);
  return `${number > 0 ? "+" : ""}${new Intl.NumberFormat("th-TH", { maximumFractionDigits: 3 }).format(number)}`;
}

function movementTimeLabel(value: string) {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return new Intl.DateTimeFormat("th-TH", {
    day: "numeric",
    month: "short",
    year: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
  }).format(date);
}

function stockState(item: GroupedStockRuntimeItem) {
  if (item.quantity_on_hand == null) return { code: "UNKNOWN", label: "ไม่มีข้อมูล" };
  if (Number(item.quantity_on_hand) <= 0) return { code: "OUT", label: "หมด" };
  return { code: "AVAILABLE", label: "" };
}

function itemMeta(item: GroupedStockRuntimeItem) {
  return [item.stock_group_name ?? "ยังไม่ได้จัดกลุ่ม", item.sku_code ? `SKU ${item.sku_code}` : null]
    .filter(Boolean)
    .join(" · ");
}

async function readAllLocalStock(identity: NonNullable<TerminalCredentialState["identity"]>) {
  const scopeKey = indexedScope(identity, "stock");
  const rows = await posRuntimeDb.stockRuntime
    .where("scopeKey")
    .equals(scopeKey)
    .and((row) => row.active)
    .toArray();

  return rows.flatMap((row) => (
    row.payload && typeof row.payload === "object"
      ? [row.payload as GroupedStockRuntimeItem]
      : []
  ));
}

async function readLocalStockItem(
  identity: NonNullable<TerminalCredentialState["identity"]>,
  stockItemId: string,
) {
  const scopeKey = indexedScope(identity, "stock");
  const row = await posRuntimeDb.stockRuntime
    .where("stockItemId")
    .equals(stockItemId)
    .and((candidate) => candidate.scopeKey === scopeKey && candidate.active)
    .first();
  return row?.payload && typeof row.payload === "object"
    ? row.payload as GroupedStockRuntimeItem
    : null;
}

async function readLocalStock(
  identity: NonNullable<TerminalCredentialState["identity"]>,
  searchText: string,
  groupFilter: string,
) {
  const allItems = await readAllLocalStock(identity);
  const query = searchText.trim();
  const searched = query
    ? await searchLocalStock(identity, query, SEARCH_LIMIT) as GroupedStockRuntimeItem[]
    : allItems;

  const items = searched.filter((item) => {
    if (groupFilter === "ALL") return true;
    if (groupFilter === "UNASSIGNED") return !item.stock_group_id;
    return item.stock_group_id === groupFilter;
  });

  const groupMap = new Map<string, LocalStockGroup>();
  for (const item of allItems) {
    if (!item.stock_group_id || !item.stock_group_name || item.stock_group_status === "INACTIVE") continue;
    groupMap.set(item.stock_group_id, {
      id: item.stock_group_id,
      name: item.stock_group_name,
      sortOrder: Number(item.stock_group_sort_order ?? 9999),
    });
  }

  const groups = [...groupMap.values()].sort((left, right) => (
    left.sortOrder - right.sortOrder || left.name.localeCompare(right.name, "th")
  ));

  return {
    items,
    groups,
    hasUnassigned: allItems.some((item) => !item.stock_group_id),
  };
}

export function StockRuntimeHost() {
  const [runtime, setRuntime] = useState<RuntimeSnapshot>({ terminal: null, session: null });
  const [mountNode, setMountNode] = useState<Element | null>(null);
  const [online, setOnline] = useState(navigator.onLine);
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [groupFilter, setGroupFilter] = useState("ALL");
  const [localStock, setLocalStock] = useState<LocalStockState | null>(null);
  const [localError, setLocalError] = useState<string | null>(null);
  const [selectedItem, setSelectedItem] = useState<GroupedStockRuntimeItem | null>(null);
  const [localDetail, setLocalDetail] = useState<LocalStockDetailState | null>(null);
  const [actionRuntime, setActionRuntime] = useState<Awaited<ReturnType<typeof readStockActionRuntime>>>(null);
  const [batchComposer, setBatchComposer] = useState<BatchComposerState | null>(null);

  useEffect(() => {
    const subscription = liveQuery(async () => ({
      terminal: await readTerminalCredential(),
      session: await readLocalEmployeeSession(),
    })).subscribe({
      next: setRuntime,
      error: () => setRuntime({ terminal: null, session: null }),
    });
    return () => subscription.unsubscribe();
  }, []);

  useEffect(() => {
    const updateMount = () => setMountNode(document.querySelector(".sale-header__actions"));
    updateMount();
    const observer = new MutationObserver(updateMount);
    observer.observe(document.body, { childList: true, subtree: true });
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    const update = () => setOnline(navigator.onLine);
    window.addEventListener("online", update);
    window.addEventListener("offline", update);
    return () => {
      window.removeEventListener("online", update);
      window.removeEventListener("offline", update);
    };
  }, []);

  const terminal = runtime.terminal;
  const session = runtime.session;
  const identity = terminal?.identity ?? null;
  const actor = session?.actor ?? null;
  const sameScope = Boolean(
    identity && actor
    && actor.organization_id === identity.organization_id
    && actor.branch_id === identity.branch_id
    && actor.node_id === identity.node_id
    && actor.runtime_session_status === "ACTIVE",
  );
  const canView = Boolean(sameScope && actor?.permission_codes.includes("stock.view"));
  const runtimeScopeKey = identity && sameScope
    ? `${identity.organization_id}:${identity.branch_id}:${identity.node_id}`
    : "";

  useEffect(() => {
    if (!identity || !canView || !runtimeScopeKey) {
      setActionRuntime(null);
      return;
    }
    const subscription = liveQuery(() => readStockActionRuntime(identity)).subscribe({
      next: setActionRuntime,
      error: () => setActionRuntime(null),
    });
    return () => subscription.unsubscribe();
  }, [canView, identity, runtimeScopeKey]);

  useEffect(() => {
    if (!open || !identity || !canView || !runtimeScopeKey) {
      setLocalStock(null);
      setLocalError(null);
      setSelectedItem(null);
      setLocalDetail(null);
      setBatchComposer(null);
      return;
    }

    setLocalError(null);
    const subscription = liveQuery(async () => {
      const [stock, cursor] = await Promise.all([
        readLocalStock(identity, query, groupFilter),
        readLocalProjectionCursor(identity, "STOCK"),
      ]);
      stock.items.sort((left, right) => {
        const leftSort = Number(left.stock_group_sort_order ?? 9999);
        const rightSort = Number(right.stock_group_sort_order ?? 9999);
        return leftSort - rightSort || left.canonical_name.localeCompare(right.canonical_name, "th");
      });
      return { ...stock, hasLocalSnapshot: cursor !== null } satisfies LocalStockState;
    }).subscribe({
      next: (value) => {
        setLocalStock(value);
        setLocalError(null);
      },
      error: () => {
        setLocalStock({ items: [], groups: [], hasUnassigned: false, hasLocalSnapshot: false });
        setLocalError("อ่านข้อมูลสต็อกไม่สำเร็จ");
      },
    });

    return () => subscription.unsubscribe();
  }, [canView, groupFilter, identity, open, query, runtimeScopeKey]);

  useEffect(() => {
    if (!open || !identity || !canView || !selectedItem) {
      setLocalDetail(null);
      return;
    }

    const stockItemId = selectedItem.stock_item_id;
    const subscription = liveQuery(async () => {
      const [item, movements, historyCursor] = await Promise.all([
        readLocalStockItem(identity, stockItemId),
        readLocalStockMovementHistory(identity, stockItemId),
        readLocalProjectionCursor(identity, "STOCK_HISTORY"),
      ]);
      return {
        item,
        movements,
        hasHistorySnapshot: historyCursor !== null,
      } satisfies LocalStockDetailState;
    }).subscribe({
      next: setLocalDetail,
      error: () => setLocalDetail({ item: null, movements: [], hasHistorySnapshot: false }),
    });

    return () => subscription.unsubscribe();
  }, [canView, identity, open, selectedItem, runtimeScopeKey]);

  useEffect(() => {
    if (!open) return;
    const closeOnEscape = (event: KeyboardEvent) => {
      if (event.key !== "Escape") return;
      if (batchComposer) setBatchComposer(null);
      else if (selectedItem) setSelectedItem(null);
      else setOpen(false);
    };
    document.addEventListener("keydown", closeOnEscape);
    return () => document.removeEventListener("keydown", closeOnEscape);
  }, [batchComposer, open, selectedItem]);

  const internalUseAction = actionRuntime?.items.find((option) => option.available && option.effect_class === "OPERATIONAL_USE") ?? null;

  async function openInternalUseBatch(seedItem: GroupedStockRuntimeItem | null = null) {
    if (!identity || !internalUseAction) return;
    const allItems = await readAllLocalStock(identity);
    allItems.sort((left, right) => left.canonical_name.localeCompare(right.canonical_name, "th"));
    setQuery("");
    setGroupFilter("ALL");
    setSelectedItem(null);
    setBatchComposer({ items: allItems, seedItem });
  }

  if (!mountNode || !terminal || !actor || !identity || !canView) return null;

  const items = localStock?.items ?? [];
  const outCount = items.filter((item) => item.quantity_on_hand != null && Number(item.quantity_on_hand) <= 0).length;
  const noLocalSnapshot = localStock != null && !localStock.hasLocalSnapshot;
  const detailItem = localDetail?.item ?? selectedItem;
  const movements = localDetail?.movements ?? [];

  function openStock() {
    setQuery("");
    setGroupFilter("ALL");
    setSelectedItem(null);
    setBatchComposer(null);
    setOpen(true);
  }

  function closeStock() {
    setBatchComposer(null);
    setSelectedItem(null);
    setOpen(false);
  }

  return (
    <>
      {createPortal(
        <button className="stock-runtime-launch sale-header__button" type="button" onClick={openStock}>
          <span aria-hidden="true">▦</span><strong>สต็อก</strong>
        </button>,
        mountNode,
      )}

      {open ? createPortal(
        <div className="pos-stock-backdrop" role="presentation">
          <section className="pos-stock-shell" role="dialog" aria-modal="true" aria-labelledby="pos-stock-title">
            <header className="pos-stock-header">
              {selectedItem ? (
                <button className="pos-stock-header__back" type="button" onClick={() => setSelectedItem(null)} aria-label="กลับไปรายการสต็อก">‹</button>
              ) : null}
              <h2 id="pos-stock-title">
                {selectedItem && detailItem ? detailItem.canonical_name : `สต็อก · ${identity.branch_name}`}
              </h2>
              <button type="button" onClick={closeStock} aria-label="ปิด">×</button>
            </header>

            {selectedItem && detailItem ? (
              <>
                {!online ? (
                  <div className="pos-stock-notice">ไม่มีอินเทอร์เน็ต · แสดงประวัติที่บันทึกไว้ล่าสุด</div>
                ) : null}
                <main className="pos-stock-detail">
                  <section className="pos-stock-detail__summary" aria-label="รายละเอียดสต็อก">
                    <div className="pos-stock-detail__balance">
                      <span>คงเหลือ</span>
                      <strong>{quantityLabel(detailItem.quantity_on_hand)}</strong>
                    </div>
                    <dl>
                      <div><dt>สาขา</dt><dd>{identity.branch_name}</dd></div>
                      <div><dt>กลุ่มสต็อก</dt><dd>{detailItem.stock_group_name ?? "ยังไม่ได้จัดกลุ่ม"}</dd></div>
                      <div><dt>SKU</dt><dd>{detailItem.sku_code ?? "—"}</dd></div>
                    </dl>
                  </section>

                  <StockActionPanel
                    identity={identity}
                    deviceSecret={terminal.deviceSecret}
                    sessionToken={session?.sessionToken ?? ""}
                    item={detailItem}
                    online={online}
                    onInternalUse={() => void openInternalUseBatch(detailItem)}
                  />

                  <section className="pos-stock-history" aria-labelledby="pos-stock-history-title">
                    <div className="pos-stock-history__heading">
                      <h3 id="pos-stock-history-title">ประวัติการเคลื่อนไหว</h3>
                      <span>{movements.length ? `${movements.length} รายการในเครื่อง` : ""}</span>
                    </div>

                    {localDetail == null ? (
                      <div className="pos-stock-empty">กำลังเปิดประวัติจากเครื่อง…</div>
                    ) : null}
                    {localDetail && !localDetail.hasHistorySnapshot && !online ? (
                      <div className="pos-stock-empty">
                        <strong>ยังไม่มีประวัติในเครื่อง</strong>
                        <span>เชื่อมต่ออินเทอร์เน็ตเพื่อซิงค์ประวัติครั้งแรก</span>
                      </div>
                    ) : null}
                    {localDetail && !localDetail.hasHistorySnapshot && online ? (
                      <div className="pos-stock-empty">
                        <strong>กำลังซิงค์ประวัติ</strong>
                        <span>หน้านี้ไม่รอเครือข่าย และจะอัปเดตอัตโนมัติเมื่อข้อมูลมาถึง</span>
                      </div>
                    ) : null}
                    {localDetail?.hasHistorySnapshot && !movements.length ? (
                      <div className="pos-stock-empty">
                        <strong>ยังไม่มีประวัติการเคลื่อนไหว</strong>
                      </div>
                    ) : null}

                    {movements.length ? (
                      <div className="pos-stock-history__timeline">
                        {movements.map((movement) => {
                          const hasExtra = Boolean(movement.actor_display_name || movement.detail_note || movement.source_label);
                          return (
                            <article className="pos-stock-movement" key={movement.movement_id}>
                              <time>{movementTimeLabel(movement.occurred_at)}</time>
                              <div className="pos-stock-movement__body">
                                <strong>{movement.reason_label}</strong>
                                {hasExtra ? (
                                  <details>
                                    <summary>รายละเอียด</summary>
                                    <dl>
                                      {movement.actor_display_name ? <div><dt>ผู้ทำรายการ</dt><dd>{movement.actor_display_name}</dd></div> : null}
                                      {movement.source_label ? <div><dt>แหล่งที่มา</dt><dd>{movement.source_label}</dd></div> : null}
                                      {movement.detail_note ? <div><dt>หมายเหตุ</dt><dd>{movement.detail_note}</dd></div> : null}
                                    </dl>
                                  </details>
                                ) : null}
                              </div>
                              <div className="pos-stock-movement__amount" data-direction={movement.direction}>
                                <strong>{deltaLabel(movement.quantity_delta)}</strong>
                                <span>{movement.unit_name}</span>
                                {movement.balance_after != null ? (
                                  <small>ยอดหลังรายการ {quantityLabel(Number(movement.balance_after))}</small>
                                ) : null}
                              </div>
                            </article>
                          );
                        })}
                      </div>
                    ) : null}
                  </section>
                </main>
              </>
            ) : (
              <>
                <div className="pos-stock-toolbar">
                  <label htmlFor="pos-stock-search">ค้นหาสต็อก</label>
                  <input
                    id="pos-stock-search"
                    value={query}
                    onChange={(event) => setQuery(event.target.value)}
                    maxLength={128}
                    placeholder="ค้นหาชื่อสินค้า SKU หรือบาร์โค้ด"
                    autoComplete="off"
                  />
                  <select
                    value={groupFilter}
                    onChange={(event) => setGroupFilter(event.target.value)}
                    aria-label="กลุ่มสต็อก"
                  >
                    <option value="ALL">ทุกกลุ่ม</option>
                    {(localStock?.groups ?? []).map((group) => (
                      <option key={group.id} value={group.id}>{group.name}</option>
                    ))}
                    {localStock?.hasUnassigned ? <option value="UNASSIGNED">ยังไม่ได้จัดกลุ่ม</option> : null}
                  </select>
                  {internalUseAction ? (
                    <button className="pos-stock-batch-launch" type="button" onClick={() => void openInternalUseBatch()}>
                      <strong>เบิกใช้หลายรายการ</strong>
                      <span>จบในหน้าเดียว</span>
                    </button>
                  ) : null}
                  <div className="pos-stock-summary" aria-live="polite">
                    <strong>{items.length}</strong><span>รายการ</span>
                    <strong>{outCount}</strong><span>หมด</span>
                  </div>
                </div>

                {!online ? (
                  <div className="pos-stock-notice">ไม่มีอินเทอร์เน็ต · แสดงข้อมูลที่บันทึกไว้ล่าสุด</div>
                ) : null}
                {localError ? <div className="pos-stock-error">{localError}</div> : null}

                <main className="pos-stock-content">
                  {localStock == null ? <div className="pos-stock-empty">กำลังเปิดข้อมูลสต็อก…</div> : null}
                  {noLocalSnapshot && !online && !localError ? (
                    <div className="pos-stock-empty">
                      <strong>ยังไม่มีข้อมูลสต็อกในเครื่อง</strong>
                      <span>เชื่อมต่ออินเทอร์เน็ตเพื่อโหลดข้อมูลครั้งแรก</span>
                    </div>
                  ) : null}
                  {noLocalSnapshot && online && !localError ? (
                    <div className="pos-stock-empty">
                      <strong>กำลังโหลดข้อมูลสต็อกล่าสุด</strong>
                      <span>เมื่อพร้อม รายการจะปรากฏอัตโนมัติ</span>
                    </div>
                  ) : null}
                  {localStock?.hasLocalSnapshot && !items.length && !localError ? (
                    <div className="pos-stock-empty">
                      <strong>{query.trim() || groupFilter !== "ALL" ? "ไม่พบสินค้าตามตัวกรอง" : "ยังไม่มีสินค้าคงคลังในสาขานี้"}</strong>
                      {query.trim() || groupFilter !== "ALL" ? <span>ลองเปลี่ยนคำค้นหรือเลือกกลุ่มอื่น</span> : null}
                    </div>
                  ) : null}

                  {items.length ? (
                    <div className="pos-stock-list">
                      {items.map((item) => {
                        const state = stockState(item);
                        const meta = itemMeta(item);
                        return (
                          <button className="pos-stock-item" type="button" key={item.stock_item_id} onClick={() => setSelectedItem(item)}>
                            <div className="pos-stock-item__identity">
                              <strong>{item.canonical_name}</strong>
                              {meta ? <span>{meta}</span> : null}
                            </div>
                            <div className="pos-stock-item__balance">
                              <small>คงเหลือ</small>
                              <div className="pos-stock-item__value">
                                <strong>{quantityLabel(item.quantity_on_hand)}</strong>
                                {state.code !== "AVAILABLE" ? (
                                  <span className="pos-stock-item__status" data-state={state.code}>{state.label}</span>
                                ) : null}
                              </div>
                            </div>
                          </button>
                        );
                      })}
                    </div>
                  ) : null}
                </main>
              </>
            )}
          </section>
        </div>,
        document.body,
      ) : null}

      {batchComposer ? createPortal(
        <StockInternalUseBatch
          identity={identity}
          deviceSecret={terminal.deviceSecret}
          sessionToken={session?.sessionToken ?? ""}
          items={batchComposer.items}
          seedItem={batchComposer.seedItem}
          online={online}
          requiresNote={Boolean(internalUseAction?.requires_note)}
          onClose={() => setBatchComposer(null)}
        />,
        document.body,
      ) : null}
    </>
  );
}
